/**
 * Выгрузка в 1С: что уйдёт в файл — ещё до вызова сервера, чтобы Финансист подтвердила
 * именно это. Считается по заявкам реестра на экране теми же правилами, что на сервере
 * (`export_to_1c`): в файл идут согласованные с остатком к оплате, налоги ЕНС не
 * выгружаются, уже выгружавшиеся — только при повторной выгрузке. Фильтры экрана в
 * выгрузке реестра не участвуют. Модуль без импортов значений — проверяется node:test
 * (`export1c.test.mjs`).
 */
import type { Export1cInput, FinPaymentRequestRow } from '@shared/api';

type Row = Pick<
  FinPaymentRequestRow,
  | 'status'
  | 'category'
  | 'source_module'
  | 'recipient_account_number'
  | 'counterparty_inn'
  | 'counterparty_name'
  | 'request_number'
  | 'amount'
  | 'approved_amount'
  | 'paid_amount'
>;

/** Бюджетный платёж — как `is_tax_payment` на сервере: в файл для банка не идёт. */
export function isTaxPayment(r: Row): boolean {
  const acc = r.recipient_account_number?.trim() ?? '';
  return (
    r.category === 'taxes' ||
    r.source_module === 'taxes_ens' ||
    (acc.length === 20 && acc.startsWith('03')) ||
    r.counterparty_inn?.trim() === '7727406020'
  );
}

/** Остаток к оплате — как `left_to_pay` на сервере. */
export function leftToPay(r: Row): number {
  const base = r.approved_amount > 0 ? r.approved_amount : r.amount;
  return Math.max(0, base - (r.paid_amount || 0));
}

/** Куда заявка попадёт при выгрузке: в файл, в файл только повторной выгрузкой, налог ЕНС
 *  (в файл не идёт) или никуда — не согласована или уже оплачена. */
type ExportClass = 'file' | 'exported' | 'tax' | 'other';

function exportClass(r: Row): ExportClass {
  const candidate = (r.status === 'approved' || r.status === 'exported_1c') && leftToPay(r) > 0.005;
  if (!candidate) return 'other';
  if (isTaxPayment(r)) return 'tax';
  return r.status === 'approved' ? 'file' : 'exported';
}

export interface ExportScope {
  /** Согласованные с остатком — пойдут в файл. */
  count: number;
  amount: number;
  /** Уже выгружались — в файл только при повторной выгрузке. */
  exportedCount: number;
  exportedAmount: number;
  /** Налоги ЕНС, которые выгрузились бы: «Заявка ФП-… (получатель)». */
  taxes: string[];
  /** Остальные: не согласованы или уже оплачены. */
  other: number;
}

export function exportScope(rows: Row[]): ExportScope {
  const scope: ExportScope = {
    count: 0,
    amount: 0,
    exportedCount: 0,
    exportedAmount: 0,
    taxes: [],
    other: 0,
  };
  for (const r of rows) {
    const cls = exportClass(r);
    if (cls === 'other') {
      scope.other += 1;
    } else if (cls === 'tax') {
      scope.taxes.push(`Заявка ${r.request_number} (${r.counterparty_name})`);
    } else if (cls === 'file') {
      scope.count += 1;
      scope.amount += leftToPay(r);
    } else {
      scope.exportedCount += 1;
      scope.exportedAmount += leftToPay(r);
    }
  }
  scope.amount = Math.round(scope.amount * 100) / 100;
  scope.exportedAmount = Math.round(scope.exportedAmount * 100) / 100;
  return scope;
}

function plural(n: number, one: string, few: string, many: string): string {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return one;
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
  return many;
}

function orders(n: number): string {
  return `${n} ${plural(n, 'платёжка', 'платёжки', 'платёжек')}`;
}

export type ExportKind = 'registry' | 'selected';

/** Вопрос перед выгрузкой согласованных; `registry` — «номер — название» реестра. */
export function confirmExportText(
  kind: ExportKind,
  scope: ExportScope,
  registry: string,
  money: (n: number) => string,
): string {
  const lines =
    kind === 'registry'
      ? [
          `Выгрузить в 1С весь реестр${registry ? ` «${registry}»` : ''}: ` +
            `${orders(scope.count)} на ${money(scope.amount)}? Фильтры на экране не учитываются.`,
        ]
      : [`Выгрузить отмеченные: ${scope.count} на ${money(scope.amount)}?`];
  if (scope.exportedCount > 0) {
    lines.push(
      `Ещё ${orders(scope.exportedCount)} уже ${plural(scope.exportedCount, 'выгружалась', 'выгружались', 'выгружались')} в 1С — в файл не войдут.`,
    );
  }
  if (kind === 'registry' && scope.taxes.length > 0) {
    lines.push(
      `Налоги ЕНС (${scope.taxes.length}) в файл не войдут: их проводят в банк-клиенте.`,
    );
  }
  if (kind === 'selected' && scope.other > 0) {
    lines.push(
      `${scope.other} из отмеченных не согласованы или уже оплачены — в файл не войдут.`,
    );
  }
  return lines.join('\n');
}

/** Вопрос о повторной выгрузке уже выгружавшихся платёжек. */
export function reexportText(kind: ExportKind, count: number): string {
  const where = kind === 'registry' ? 'этого реестра' : 'из отмеченных';
  const was = plural(count, 'выгружалась', 'выгружались', 'выгружались');
  const them = plural(count, 'её', 'их', 'их');
  return (
    `${orders(count)} ${where} уже ${was} в 1С. Выгрузить ${them} повторно? ` +
    'Если файл уже загружен в банк, платежи задвоятся.'
  );
}

/** Нечего выгружать — почему (без вызова сервера). */
export function nothingToExportText(scope: ExportScope): string {
  if (scope.taxes.length > 0) {
    return `В файл для банка нечего выгружать. Налоги ЕНС проводятся в банк-клиенте:\n${scope.taxes.join('\n')}`;
  }
  return 'В файл для банка нечего выгружать: нет согласованных заявок с остатком к оплате.';
}

/** Отмечены налоги ЕНС — сервер их в файл не возьмёт: снять отметку. */
export function taxesSelectedText(scope: ExportScope): string {
  return (
    'Налоги ЕНС в файл для банка не выгружаются: в нём нет полей бюджетного платежа ' +
    `(статус плательщика, КБК, ОКТМО). Сделайте эти платежи в банк-клиенте и снимите с них отметку:\n${scope.taxes.join('\n')}`
  );
}

/** Строка заявки для выгрузки: поля для подсчёта, её id и реестр. */
export type ExportRow = Row & Pick<FinPaymentRequestRow, 'id' | 'registry_id'>;

/** Выбранный реестр: id и подпись «номер — название» (пусто, если подписи нет). */
export interface ExportRegistry {
  id: string;
  label: string;
}

/** Вопрос перед выгрузкой и то, что после «Выгрузить» уйдёт на сервер. */
export interface ExportAsk {
  kind: ExportKind;
  /** Повторная выгрузка уже выгружавшихся — опасный вариант. */
  reexport: boolean;
  text: string;
  /** Ровно заявки из вопроса: сервер выгрузит их пересечение с реестром. */
  payload: Export1cInput;
}

/** Вопрос о выгрузке — или почему выгружать нечего. */
export type ExportPlan = { ask: ExportAsk } | { notice: string };

/**
 * Что спросить перед выгрузкой и что отправить — из одного набора строк: отмеченных или
 * всего реестра, и только строк выбранного реестра (прежний ответ по другому реестру в счёт
 * не идёт). В `request_ids` — заявки, посчитанные в вопросе, без налогов ЕНС: сервер
 * выгрузит их пересечение с реестром, то есть ровно то, что подтвердили.
 */
export function planExport(
  kind: ExportKind,
  rows: ExportRow[],
  selectedIds: string[],
  registry: ExportRegistry | null,
  money: (n: number) => string,
): ExportPlan {
  const inRegistry = registry ? rows.filter((r) => r.registry_id === registry.id) : rows;
  const counted =
    kind === 'selected' ? inRegistry.filter((r) => selectedIds.includes(r.id)) : inRegistry;
  const scope = exportScope(counted);
  if (kind === 'selected' && scope.taxes.length > 0) {
    return { notice: taxesSelectedText(scope) };
  }
  const ids = (cls: ExportClass) => counted.filter((r) => exportClass(r) === cls).map((r) => r.id);
  const where = registry ? { registry_id: registry.id } : {};
  if (scope.count > 0) {
    return {
      ask: {
        kind,
        reexport: false,
        text: confirmExportText(kind, scope, registry?.label ?? '', money),
        payload: { request_ids: ids('file'), ...where },
      },
    };
  }
  if (scope.exportedCount > 0) {
    return {
      ask: {
        kind,
        reexport: true,
        text: reexportText(kind, scope.exportedCount),
        payload: { request_ids: ids('exported'), ...where, include_exported: true },
      },
    };
  }
  return { notice: nothingToExportText(scope) };
}

/**
 * Сервер ответил 409: среди подтверждённых заявок новых нет, все уже выгружались (список на
 * экране устарел). Вопрос о повторной выгрузке — о тех же заявках, словами сервера: в них
 * точное число и сумма.
 */
export function reexportAfterConflict(ask: ExportAsk, serverText: string): ExportAsk {
  return {
    kind: ask.kind,
    reexport: true,
    text: `${serverText.trim().replace(/[.!]+$/, '')}. Выгрузить их повторно?`,
    payload: { ...ask.payload, include_exported: true },
  };
}

/** Байты файла из base64 ответа сервера. */
export function base64ToBytes(b64: string): Uint8Array {
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/** Имя файла выгрузки; `today` — «2026-11-03» по местному времени (`todayIso`). */
export function exportFileName(today: string): string {
  return `1c_payment_orders_${today}.txt`;
}
